import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { 
  FileText, 
  Save, 
  Send, 
  Calendar as CalendarIcon,
  Plus,
  X,
  Stethoscope,
  ClipboardList,
  Activity,
  Pill
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Common CPT codes for telehealth
const CPT_CODES = [
  { code: "99201", description: "Office visit, new patient, minimal (10 min)" },
  { code: "99202", description: "Office visit, new patient, low (20 min)" },
  { code: "99203", description: "Office visit, new patient, moderate (30 min)" },
  { code: "99204", description: "Office visit, new patient, moderate-high (45 min)" },
  { code: "99205", description: "Office visit, new patient, high (60 min)" },
  { code: "99211", description: "Office visit, established, minimal (5 min)" },
  { code: "99212", description: "Office visit, established, low (10 min)" },
  { code: "99213", description: "Office visit, established, low-moderate (15 min)" },
  { code: "99214", description: "Office visit, established, moderate (30 min)" },
  { code: "99215", description: "Office visit, established, high (40 min)" },
  { code: "99441", description: "Telephone E/M, 5-10 min" },
  { code: "99442", description: "Telephone E/M, 11-20 min" },
  { code: "99443", description: "Telephone E/M, 21-30 min" },
  { code: "G2012", description: "Virtual check-in, 5-10 min" },
  { code: "G2010", description: "Remote evaluation of images" },
];

// Common ICD-10 codes
const ICD_CODES = [
  { code: "Z00.00", description: "General adult medical exam" },
  { code: "Z00.01", description: "General exam with abnormal findings" },
  { code: "I10", description: "Essential hypertension" },
  { code: "E11.9", description: "Type 2 diabetes without complications" },
  { code: "E11.65", description: "Type 2 diabetes with hyperglycemia" },
  { code: "J06.9", description: "Acute upper respiratory infection" },
  { code: "M54.5", description: "Low back pain" },
  { code: "F32.9", description: "Major depressive disorder" },
  { code: "F41.1", description: "Generalized anxiety disorder" },
  { code: "R05.9", description: "Cough, unspecified" },
  { code: "R51.9", description: "Headache" },
  { code: "K21.0", description: "GERD with esophagitis" },
  { code: "J45.20", description: "Mild intermittent asthma" },
  { code: "E78.5", description: "Hyperlipidemia" },
  { code: "N39.0", description: "Urinary tract infection" },
];

const sessionNotesSchema = z.object({
  chief_complaint: z.string().min(1, "Chief complaint is required").max(500),
  subjective: z.string().max(2000).optional(),
  objective: z.string().max(2000).optional(),
  assessment: z.string().max(2000).optional(),
  plan: z.string().max(2000).optional(),
  follow_up_notes: z.string().max(1000).optional(),
});

type SessionNotesFormData = z.infer<typeof sessionNotesSchema>;

interface SessionDocumentationFormProps {
  sessionId?: string;
  patientName?: string;
  sessionDate?: Date;
  duration?: number;
  onSave?: (data: any) => Promise<void> | void;
  onSubmit?: (data: any) => Promise<void> | void;
  initialSoapNotes?: {
    chief_complaint: string;
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
    cpt_codes?: string[];
    icd_codes?: string[];
    follow_up_date?: string;
    follow_up_notes?: string;
  };
}

export function SessionDocumentationForm({
  sessionId,
  patientName = "Patient Name",
  sessionDate = new Date(),
  duration = 30,
  onSave,
  onSubmit,
  initialSoapNotes,
}: SessionDocumentationFormProps) {
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>(initialSoapNotes?.cpt_codes || []);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState<string[]>(initialSoapNotes?.icd_codes || []);
  const [followUpDate, setFollowUpDate] = useState<Date | undefined>(
    initialSoapNotes?.follow_up_date ? new Date(initialSoapNotes.follow_up_date) : undefined
  );
  const [cptSearch, setCptSearch] = useState("");
  const [icdSearch, setIcdSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<SessionNotesFormData>({
    resolver: zodResolver(sessionNotesSchema),
    defaultValues: {
      chief_complaint: initialSoapNotes?.chief_complaint || "",
      subjective: initialSoapNotes?.subjective || "",
      objective: initialSoapNotes?.objective || "",
      assessment: initialSoapNotes?.assessment || "",
      plan: initialSoapNotes?.plan || "",
      follow_up_notes: initialSoapNotes?.follow_up_notes || "",
    },
  });

  // Re-hydrate form when initialSoapNotes changes (e.g. fetched from DB)
  useEffect(() => {
    if (initialSoapNotes) {
      form.reset({
        chief_complaint: initialSoapNotes.chief_complaint || "",
        subjective: initialSoapNotes.subjective || "",
        objective: initialSoapNotes.objective || "",
        assessment: initialSoapNotes.assessment || "",
        plan: initialSoapNotes.plan || "",
        follow_up_notes: initialSoapNotes.follow_up_notes || "",
      });
      if (initialSoapNotes.cpt_codes?.length) setSelectedCptCodes(initialSoapNotes.cpt_codes);
      if (initialSoapNotes.icd_codes?.length) setSelectedIcdCodes(initialSoapNotes.icd_codes);
      if (initialSoapNotes.follow_up_date) setFollowUpDate(new Date(initialSoapNotes.follow_up_date));
    }
  }, [initialSoapNotes]);

  const filteredCptCodes = CPT_CODES.filter(
    (cpt) =>
      cpt.code.includes(cptSearch) ||
      cpt.description.toLowerCase().includes(cptSearch.toLowerCase())
  );

  const filteredIcdCodes = ICD_CODES.filter(
    (icd) =>
      icd.code.toLowerCase().includes(icdSearch.toLowerCase()) ||
      icd.description.toLowerCase().includes(icdSearch.toLowerCase())
  );

  const addCptCode = (code: string) => {
    if (!selectedCptCodes.includes(code)) {
      setSelectedCptCodes([...selectedCptCodes, code]);
    }
    setCptSearch("");
  };

  const removeCptCode = (code: string) => {
    setSelectedCptCodes(selectedCptCodes.filter((c) => c !== code));
  };

  const addIcdCode = (code: string) => {
    if (!selectedIcdCodes.includes(code)) {
      setSelectedIcdCodes([...selectedIcdCodes, code]);
    }
    setIcdSearch("");
  };

  const removeIcdCode = (code: string) => {
    setSelectedIcdCodes(selectedIcdCodes.filter((c) => c !== code));
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    const formData = form.getValues();
    const data = {
      ...formData,
      cpt_codes: selectedCptCodes,
      icd_codes: selectedIcdCodes,
      follow_up_date: followUpDate,
      session_id: sessionId,
    };
    
    try {
      await onSave?.(data);
      toast.success("Draft saved successfully");
    } catch {
      // error toast handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitNotes = async (formData: SessionNotesFormData) => {
    if (selectedCptCodes.length === 0) {
      toast.error("Please select at least one CPT code");
      return;
    }
    if (selectedIcdCodes.length === 0) {
      toast.error("Please select at least one ICD code");
      return;
    }

    setIsSubmitting(true);
    const data = {
      ...formData,
      cpt_codes: selectedCptCodes,
      icd_codes: selectedIcdCodes,
      follow_up_date: followUpDate,
      session_id: sessionId,
    };

    try {
      await onSubmit?.(data);
      toast.success("Session notes submitted successfully");
    } catch {
      // error toast handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const billingUnits = Math.ceil(duration / 15);

  return (
    <div className="space-y-6">
      {/* Session Summary Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Stethoscope className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{patientName}</h2>
                <p className="text-sm text-muted-foreground">
                  {format(sessionDate, "MMMM d, yyyy")} • {duration} minutes • {billingUnits} billing units
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSaveDraft} disabled={isSaving}>
                <Save className="mr-2 h-4 w-4" />
                {isSaving ? "Saving..." : "Save Draft"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmitNotes)} className="space-y-6">
          <Tabs defaultValue="soap" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="soap" className="gap-2">
                <ClipboardList className="h-4 w-4" />
                SOAP Notes
              </TabsTrigger>
              <TabsTrigger value="coding" className="gap-2">
                <FileText className="h-4 w-4" />
                Billing Codes
              </TabsTrigger>
              <TabsTrigger value="followup" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                Follow-up
              </TabsTrigger>
            </TabsList>

            {/* SOAP Notes Tab */}
            <TabsContent value="soap" className="space-y-6">
              {/* Chief Complaint */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Chief Complaint *
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="chief_complaint"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            placeholder="Patient's primary reason for the visit..."
                            className="min-h-[80px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* SOAP Grid */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Subjective */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Subjective</CardTitle>
                    <CardDescription>Patient's description of symptoms and history</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FormField
                      control={form.control}
                      name="subjective"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              placeholder="History of present illness, review of systems, past medical history..."
                              className="min-h-[150px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                {/* Objective */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Objective</CardTitle>
                    <CardDescription>Clinical observations and exam findings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FormField
                      control={form.control}
                      name="objective"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              placeholder="Vital signs, physical exam findings, lab results, imaging..."
                              className="min-h-[150px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                {/* Assessment */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Assessment</CardTitle>
                    <CardDescription>Clinical impression and diagnoses</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FormField
                      control={form.control}
                      name="assessment"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              placeholder="Diagnosis, differential diagnoses, clinical reasoning..."
                              className="min-h-[150px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                {/* Plan */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Pill className="h-4 w-4" />
                      Plan
                    </CardTitle>
                    <CardDescription>Treatment plan and recommendations</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FormField
                      control={form.control}
                      name="plan"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              placeholder="Medications, therapies, referrals, patient education..."
                              className="min-h-[150px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Billing Codes Tab */}
            <TabsContent value="coding" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* CPT Codes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">CPT Codes *</CardTitle>
                    <CardDescription>Procedure codes for billing</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Selected CPT Codes */}
                    {selectedCptCodes.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedCptCodes.map((code) => {
                          const cptData = CPT_CODES.find((c) => c.code === code);
                          return (
                            <Badge key={code} variant="secondary" className="gap-1 py-1">
                              <span className="font-mono font-semibold">{code}</span>
                              <button
                                type="button"
                                onClick={() => removeCptCode(code)}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    {/* Search CPT */}
                    <div className="space-y-2">
                      <Input
                        placeholder="Search CPT codes..."
                        value={cptSearch}
                        onChange={(e) => setCptSearch(e.target.value)}
                      />
                      {cptSearch && (
                        <div className="max-h-48 overflow-y-auto rounded-md border">
                          {filteredCptCodes.map((cpt) => (
                            <button
                              key={cpt.code}
                              type="button"
                              onClick={() => addCptCode(cpt.code)}
                              disabled={selectedCptCodes.includes(cpt.code)}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center justify-between",
                                selectedCptCodes.includes(cpt.code) && "opacity-50"
                              )}
                            >
                              <div>
                                <span className="font-mono font-semibold">{cpt.code}</span>
                                <span className="ml-2 text-muted-foreground">{cpt.description}</span>
                              </div>
                              <Plus className="h-4 w-4 text-muted-foreground" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* ICD Codes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">ICD-10 Codes *</CardTitle>
                    <CardDescription>Diagnosis codes</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Selected ICD Codes */}
                    {selectedIcdCodes.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedIcdCodes.map((code) => {
                          const icdData = ICD_CODES.find((c) => c.code === code);
                          return (
                            <Badge key={code} variant="outline" className="gap-1 py-1">
                              <span className="font-mono font-semibold">{code}</span>
                              <button
                                type="button"
                                onClick={() => removeIcdCode(code)}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    {/* Search ICD */}
                    <div className="space-y-2">
                      <Input
                        placeholder="Search ICD-10 codes..."
                        value={icdSearch}
                        onChange={(e) => setIcdSearch(e.target.value)}
                      />
                      {icdSearch && (
                        <div className="max-h-48 overflow-y-auto rounded-md border">
                          {filteredIcdCodes.map((icd) => (
                            <button
                              key={icd.code}
                              type="button"
                              onClick={() => addIcdCode(icd.code)}
                              disabled={selectedIcdCodes.includes(icd.code)}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center justify-between",
                                selectedIcdCodes.includes(icd.code) && "opacity-50"
                              )}
                            >
                              <div>
                                <span className="font-mono font-semibold">{icd.code}</span>
                                <span className="ml-2 text-muted-foreground">{icd.description}</span>
                              </div>
                              <Plus className="h-4 w-4 text-muted-foreground" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Billing Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Billing Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg bg-muted p-4">
                      <p className="text-sm text-muted-foreground">Duration</p>
                      <p className="text-2xl font-bold">{duration} min</p>
                    </div>
                    <div className="rounded-lg bg-muted p-4">
                      <p className="text-sm text-muted-foreground">Billing Units</p>
                      <p className="text-2xl font-bold">{billingUnits}</p>
                    </div>
                    <div className="rounded-lg bg-muted p-4">
                      <p className="text-sm text-muted-foreground">Primary CPT</p>
                      <p className="text-2xl font-bold font-mono">
                        {selectedCptCodes[0] || "—"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Follow-up Tab */}
            <TabsContent value="followup" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Schedule Follow-up</CardTitle>
                  <CardDescription>Plan the next appointment if needed</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Follow-up Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !followUpDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {followUpDate ? format(followUpDate, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={followUpDate}
                            onSelect={setFollowUpDate}
                            disabled={(date) => date < new Date()}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Recommended Timeframe</Label>
                      <Select onValueChange={(value) => {
                        const days = parseInt(value);
                        const date = new Date();
                        date.setDate(date.getDate() + days);
                        setFollowUpDate(date);
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Quick select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">1 week</SelectItem>
                          <SelectItem value="14">2 weeks</SelectItem>
                          <SelectItem value="30">1 month</SelectItem>
                          <SelectItem value="60">2 months</SelectItem>
                          <SelectItem value="90">3 months</SelectItem>
                          <SelectItem value="180">6 months</SelectItem>
                          <SelectItem value="365">1 year</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="follow_up_notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Follow-up Instructions</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Notes for the follow-up visit, what to monitor, when to return sooner..."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Submit Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {selectedCptCodes.length === 0 || selectedIcdCodes.length === 0 ? (
                    <span className="text-destructive">
                      * Please add at least one CPT and ICD code before submitting
                    </span>
                  ) : (
                    "All required fields completed"
                  )}
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={isSaving}>
                    <Save className="mr-2 h-4 w-4" />
                    Save Draft
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    <Send className="mr-2 h-4 w-4" />
                    {isSubmitting ? "Submitting..." : "Submit Notes"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>
    </div>
  );
}
